import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import BulkActionModel, {
  type BulkActionCustomAddToJournal,
} from 'donor-elf-web/models/bulk-action';
import JournalService from 'donor-elf-web/services/journal-service';
import type { BulkActionsContactsChangedArgs } from '.';

interface ComponentArgs {
  contactIds: string[];
  onContactsChanged: (args: BulkActionsContactsChangedArgs) => void;
  onClose: () => void;
}

export default class ContactBulkActionsAddToJournalComponent extends Component<ComponentArgs> {
  @service declare journalService: JournalService;
  @service declare store: Store;

  @tracked addToJournalId?: string;
  @tracked showSelectJournalError = false;

  bulkAction?: BulkActionModel;
  @tracked waitingOnBulkActionToComplete = false;
  @tracked saveError = false;

  @tracked bulkActionComplete = false;

  get journalOptions(): SelectOption[] {
    return sortByProperties(this.journalService.journals, [
      'active:desc',
      'startDate:desc',
    ]).map((journal) => {
      let label = journal.label;
      if (!journal.active) {
        label += ' (inactive)';
      }
      return {
        val: journal.id,
        label,
      };
    });
  }

  get addToJournalName() {
    if (this.addToJournalId == null) {
      return undefined;
    }
    return this.journalOptions.find((x) => x.val === this.addToJournalId)
      ?.label;
  }

  @action
  handleJournalChanged(journalId: string) {
    this.addToJournalId = journalId;
    this.showSelectJournalError = false;
  }

  @action
  async save() {
    this.showSelectJournalError = false;
    if (this.addToJournalId == null) {
      this.showSelectJournalError = true;
      return;
    }

    this.waitingOnBulkActionToComplete = true;
    this.bulkAction = this.store.createRecord('bulk-action');
    this.bulkAction.actionType = 'add_to_journal';
    (this.bulkAction.custom as BulkActionCustomAddToJournal) = {
      contact_ids: this.args.contactIds,
      journal_id: this.addToJournalId,
    };
    try {
      await this.bulkAction.save();
    } catch (error: any) {
      this.saveError = true;
    }
  }

  @action
  async handleBulkActionStatusChange(bulkAction: BulkActionModel) {
    if (bulkAction.status === 'completed') {
      await this.journalService.load({ forceReload: true });
      this.waitingOnBulkActionToComplete = false;
      this.bulkActionComplete = true;
      this.args.onContactsChanged({
        contactIds: this.args.contactIds,
        bulkActionType: 'add_to_journal',
      });
      await bulkAction.destroyRecord();
      this.args.onClose();
    } else if (bulkAction.status === 'error') {
      this.saveError = true;
    }
  }
}
